import { User } from '../user/userTypes';

export const NOTE_AUTO_SAVE_TIMEOUT = 1000;

type LocationCoordinates = {
	coordinates: [number, number];
	name?: string;
	type?: string;
};

type BusinessContact = {
	name: string;
	titles: string[];
};

interface Location {
	name: string;
	location: LocationCoordinates;
	type: string;
	street: string;
	city: string;
	radius: number;
	zip: number;
	id: string;
	userAdded?: boolean;
	userId?: string;
}

interface List {
	businessIds: string[];
	location?: LocationCoordinates;
	name: string;
	id: string;
	userId: string;
	smartList?: boolean;
	deleted: boolean;
	updatedAt: string;
	createdAt: string;
}

interface Activity {
	businessId: string;
	companyId: string;
	createdAt: string;
	id: string;
	listId: string;
	noteId: string;
	type: string;
	updatedAt: string;
	userId: string;
	data?: {
		status?: boolean;
		text?: string;
		emailId?: string;
		inboundEmailId?: string;
		listId?: string;
		listName?: string;
	};
	header: string;
}

interface UserNotes {
	isContacted?: boolean;
	lead?: boolean;
	contactType?: ContactType;
	note?: string;
	userAddedData?: Array<{
		type: string;
		checked: boolean;
		data: any;
	}>;
}

type Hours = {
	[key: string]: string;
};

interface ContactType {
	emailPrimary?: boolean;
	emailSecondary?: boolean;
	thirdEmail?: boolean;
	phone?: boolean;
	messenger?: boolean;
	instagramMessenger?: boolean;
	website?: boolean;
	yelp?: boolean;
	google?: boolean;
	facebook?: boolean;
	instagram?: boolean;
	pinterest?: boolean;
	twitter?: boolean;
	youtube?: boolean;
	snapchat?: boolean;
	tiktok?: boolean;
}

const defaultContactType = {
	emailPrimary: false,
	emailSecondary: false,
	thirdEmail: false,
	phone: false,
	messenger: false,
	instagramMessenger: false,
	website: false,
	yelp: false,
	google: false,
	facebook: false,
	instagram: false,
	pinterest: false,
	twitter: false,
	youtube: false,
	snapchat: false,
	tiktok: false
};

interface UserAddedData {
	checked: boolean;
	data: string;
	type: string;
	emailLowPriority?: boolean;
}

interface Business extends UserNotes {
	_id: string;
	id?: string;
	businessId?: string;
	listId?: string;
	listName?: string;
	lastContact?: any;
	emailStatus: string;
	emailStatusCount: number;
	emailSubject: string;
	emailBody: string;
	scheduledEmailSubject?: string;
	scheduledEmailBody?: string;
	emailBcc: string;
	emailLowPriority?: boolean;
	hasActiveCampaign?: boolean;
	yearIncorporated?: number;
	yearInBusiness?: number;
	address: {
		address: string;
		street: string;
		city: string;
		state: string;
		zip: string;
		location: {
			type?: string;
			coordinates: [number, number];
		};
	};
	categories: {
		display: string;
		search: string[];
		searchString: string;
	};
	displayCategory: string;
	hours?: Hours;
	chain: boolean;
	distance: number;
	email: string;
	nylasThreadId: string;
	name: string;
	locations: number;
	phone: {
		display: string;
		phone: string;
	};
	score: number;
	web: {
		email: string;
		emailLowPriority?: boolean;
	};
	website: string;
	yearsInBusiness: number;
	yelp?: {
		id: string;
		rating: number;
		reviews: number;
		url?: string;
		thumnnailURL?: string;
		about?: string;
	};
	facebook?: {
		url: string;
		email: string;
		messengerUrl?: string;
		website?: string;
		emailLowPriority?: boolean;
	};
	contactNames?: BusinessContact[];
	activityNotes?: {
		text: string;
		updatedAt: string;
	}[];
	businessBureau?:
		| {
				url: string;
				alternateBusinessNames: string[];
				businessManagersInfo: string[];
				headquarterAddress: string;
				phone: string;
				rating: string;
				typeOfEntity?: string;
				website: string;
				businessManagers: string[];
				contactInfos: string[];
				yearsInBusiness: string;
				workingHours: any;
		  }
		| boolean;
	userAddedData?: UserAddedData[] | undefined;
	customData?: {
		numEmployees?: string;
		salesVolume?: string;
		sqftRangeEstimateMax?: number;
		sqftRangeEstimateMin?: number;
		buildingFootprintSqft?: number;
		sqftRangeEstimateReasoning?: string;
	};
	dataSources: {
		public: boolean;
		government: boolean;
		financial: Array<string>;
		other: Array<string>;
	};
	reminder?: {
		hasReminderTaskSet: boolean;
		reminderTriggerDate?: string;
		reminderText?: string;
	};
	socials?: {
		instagram?: string;
		twitter?: string;
		tiktok?: string;
		snapchat?: string;
		youtube?: string;
		pinterest?: string;
	};
	threadedEmails?: Array<{
		id: string;
		object: string;
		account_id: string;
		subject: string;
		from: [
			{
				name: string;
				email: string;
				original_email: string;
			}
		];

		to: [
			{
				name: string;
				email: string;
			}
		];
		date: number;
		thread_id: string;
		snippet: string;
		body: string;
		unread: false;
		starred: false;
		folder: {
			id: string;
			name: string;
			display_name: string;
			bodyText: string;
			messageId: string;
		};
	}>;
}

enum SearchType {
	INCLUDES,
	EXCLUDES
}

interface Search {
	type: SearchType;
	value: string;
	index: number;
}

enum FilterType {
	INCLUDES,
	EXCLUDES,
	NEUTRAL
}

enum FilterQueryType {
	SAVED_SEARCH,
	LOCATION,
	LIST,
	FILTER,
	EXPORT
}

interface Filter {
	type?: FilterType;
	value: string;
}

interface BusinessQuery {
	businessSortingTestVariant?: string;
	businessIds?: string[];
	coordinates?: [number, number] | string;
	category?: string;
	minDistance?: number;
	maxDistance?: number;
	minYelpReviews?: number;
	maxYelpReviews?: number;
	limit: number;
	searchName?: string;
	searches?: Search[];
	includes?: string[];
	excludes?: string[];
	skip: number;
	skipPrev?: number;
	chain?: boolean | number;
	sortField?: string;
	sort?: Record<string, '1' | '-1'>;
	count?: boolean;
	scheduledYes?: boolean;
	scheduledNo?: boolean;
	isInListYes?: boolean;
	isInListNo?: boolean;
	listId?: string;

	haveRE2?: boolean;
	includeWebsitesYes?: boolean;
	includeWebsitesNo?: boolean;
	contactedYes?: boolean;
	contactedNo?: boolean;
	includeFBMessenger?: boolean;
	includeIGMessenger?: boolean;
	includePOIs?: boolean;
	excludePOIs?: boolean;
	includeEmailsYes?: boolean;
	includeEmailsNo?: boolean;
	includePhoneNumber?: boolean;
	revertIncludeEmailsYes?: boolean;
	chains?: boolean;
	nonChains?: boolean;
	notesSaved?: boolean;
	lastIndex?: number;
	countTwoTen?: boolean;
	countOne?: boolean;
	lead?: boolean;
	countTenPlus?: boolean;
	queryType?: FilterQueryType;
	queryTypePrev?: FilterQueryType;
	startDate?: number;
	endDate?: number;

	contactedEmail?: boolean;
	contactedMessenger?: boolean;
	contactedInstagramMessenger?: boolean;
	contactedPhone?: boolean;
	repliedYes?: boolean;
	repliedNo?: boolean;
	openCountZero?: boolean;
	openCountOneFour?: boolean;
	openCountFiveEight?: boolean;
	openCountNinePlus?: boolean;
	leadYes?: boolean;
	leadNo?: boolean;
	leadUnmarked?: boolean;

	employees1To49?: boolean;
	employees50To99?: boolean;
	employees100To249?: boolean;
	employees250To499?: boolean;
	employeesOver500?: boolean;

	salesVolumeLessThan5M?: boolean;
	salesVolume5To10M?: boolean;
	salesVolume10To20M?: boolean;
	salesVolume20To50M?: boolean;
	salesVolume50To100M?: boolean;
	salesVolumeOver100M?: boolean;

	sqftSmall?: boolean;
	sqftMedium?: boolean;
	sqftLarge?: boolean;

	// New property for selected location
	locationName?: string;
	state?: string | null;
	country?: string | null;
	selectedLocation?: string;

	cursor?: [number, string] | [string, string];

	onlyContactedForExport?: boolean;
	user?: User;
}

enum DashboardActionTypes {
	DASHBOARD_SET_QUERY = 'dashboard/DASHBOARD_SET_QUERY',

	DASHBOARD_RESET_QUERY = 'dashboard/DASHBOARD_RESET_QUERY',
	DASHBOARD_RESET_QUERY_PARTIAL = 'dashboard/DASHBOARD_RESET_QUERY_PARTIAL',

	DASHBOARD_SELECT_LOCATION = 'dashboard/DASHBOARD_SELECT_LOCATION',

	DASHBOARD_SELECT_BUSINESS = 'dashboard/DASHBOARD_SELECT_BUSINESS',

	DASHBOARD_GET_BUSINESS_REQUEST = 'dashboard/DASHBOARD_GET_BUSINESS_REQUEST',
	DASHBOARD_GET_BUSINESS_SUCCESS = 'dashboard/DASHBOARD_GET_BUSINESS_SUCCESS',

	DASHBOARD_GET_BUSINESSES_REQUEST = 'dashboard/DASHBOARD_GET_BUSINESSES_REQUEST',
	DASHBOARD_GET_BUSINESSES_SUCCESS = 'dashboard/DASHBOARD_GET_BUSINESSES_SUCCESS',
	DASHBOARD_GET_BUSINESSES_FAILURE = 'dashboard/DASHBOARD_GET_BUSINESSES_FAILURE',

	DASHBOARD_UPDATE_BUSINESS_NOTES_REQUEST = 'dashboard/DASHBOARD_UPDATE_BUSINESS_NOTES_REQUEST',
	DASHBOARD_UPDATE_BUSINESS_NOTES_SUCCESS = 'dashboard/DASHBOARD_UPDATE_BUSINESS_NOTES_SUCCESS',
	DASHBOARD_UPDATE_BUSINESS_NOTES_FAILURE = 'dashboard/DASHBOARD_UPDATE_BUSINESS_NOTES_FAILURE',

	DASHBOARD_SELECT_LIST = 'dashboard/DASHBOARD_SELECT_LIST',

	DASHBOARD_GET_LISTS_REQUEST = 'dashboard/DASHBOARD_GET_LISTS_REQUEST',
	DASHBOARD_GET_LISTS_SUCCESS = 'dashboard/DASHBOARD_GET_LISTS_SUCCESS',
	DASHBOARD_GET_LISTS_FAILURE = 'dashboard/DASHBOARD_GET_LISTS_FAILURE',

	DASHBOARD_GET_SUBORDINATE_LISTS_REQUEST = 'dashboard/DASHBOARD_GET_SUBORDINATE_LISTS_REQUEST',
	DASHBOARD_GET_SUBORDINATE_LISTS_SUCCESS = 'dashboard/DASHBOARD_GET_SUBORDINATE_LISTS_SUCCESS',
	DASHBOARD_GET_SUBORDINATE_LISTS_FAILURE = 'dashboard/DASHBOARD_GET_SUBORDINATE_LISTS_FAILURE',

	DASHBOARD_ADD_LIST_REQUEST = 'dashboard/DASHBOARD_ADD_LIST_REQUEST',
	DASHBOARD_ADD_LIST_SUCCESS = 'dashboard/DASHBOARD_ADD_LIST_SUCCESS',
	DASHBOARD_ADD_LIST_LOADING = 'dashboard/DASHBOARD_ADD_LIST_LOADING',
	DASHBOARD_ADD_LIST_STALE = 'dashboard/DASHBOARD_ADD_LIST_STALE',
	DASHBOARD_ADD_LIST_FAILURE = 'dashboard/DASHBOARD_ADD_LIST_FAILURE',

	DASHBOARD_EDIT_LIST_REQUEST = 'dashboard/DASHBOARD_EDIT_LIST_REQUEST',
	DASHBOARD_EDIT_LIST_SUCCESS = 'dashboard/DASHBOARD_EDIT_LIST_SUCCESS',
	DASHBOARD_EDIT_LIST_FAILURE = 'dashboard/DASHBOARD_EDIT_LIST_FAILURE',

	DASHBOARD_ADD_BUSINESS_TO_LIST_REQUEST = 'dashboard/DASHBOARD_ADD_BUSINESS_TO_LIST_REQUEST',
	DASHBOARD_ADD_BUSINESS_TO_LIST_SUCCESS = 'dashboard/DASHBOARD_ADD_BUSINESS_TO_LIST_SUCCESS',
	DASHBOARD_ADD_BUSINESS_TO_LIST_FAILURE = 'dashboard/DASHBOARD_ADD_BUSINESS_TO_LIST_FAILURE',

	DASHBOARD_REMOVE_BUSINESS_FROM_LIST_REQUEST = 'dashboard/DASHBOARD_REMOVE_BUSINESS_FROM_LIST_REQUEST',
	DASHBOARD_REMOVE_BUSINESS_FROM_LIST_SUCCESS = 'dashboard/DASHBOARD_REMOVE_BUSINESS_FROM_LIST_SUCCESS',
	DASHBOARD_REMOVE_BUSINESS_FROM_LIST_FAILURE = 'dashboard/DASHBOARD_REMOVE_BUSINESS_FROM_LIST_FAILURE',

	DASHBOARD_REMOVE_LIST_REQUEST = 'dashboard/DASHBOARD_REMOVE_LIST_REQUEST',
	DASHBOARD_REMOVE_LIST_SUCCESS = 'dashboard/DASHBOARD_REMOVE_LIST_SUCCESS',
	DASHBOARD_REMOVE_LIST_FAILURE = 'dashboard/DASHBOARD_REMOVE_LIST_FAILURE',

	DASHBOARD_REMOVE_BATCH_LISTS_REQUEST = 'dashboard/DASHBOARD_REMOVE_BATCH_LISTS_REQUEST',
	DASHBOARD_REMOVE_BATCH_LISTS_SUCCESS = 'dashboard/DASHBOARD_REMOVE_BATCH_LISTS_SUCCESS',
	DASHBOARD_REMOVE_BATCH_LISTS_FAILURE = 'dashboard/DASHBOARD_REMOVE_BATCH_LISTS_FAILURE',

	DASHBOARD_EXPORT_BUSINESSES_REQUEST = 'dashboard/DASHBOARD_EXPORT_BUSINESSES_REQUEST',
	DASHBOARD_EXPORT_BUSINESSES_SUCCESS = 'dashboard/DASHBOARD_EXPORT_BUSINESSES_SUCCESS',
	DASHBOARD_EXPORT_BUSINESSES_FAILURE = 'dashboard/DASHBOARD_EXPORT_BUSINESSES_FAILURE',

	SET_SENT_EMAILS_IN_BUSINESSES = 'dashboard/SET_SENT_EMAILS_IN_BUSINESSES',

	DASHBOARD_SET_BUSINESS_REMINDER = 'dashboard/DASHBOARD_SET_BUSINESS_REMINDER',
	DASHBOARD_SET_BUSINESS_REMINDER_FAILURE = 'dashboard/DASHBOARD_SET_BUSINESS_REMINDER_FAILURE',
	DASHBOARD_SET_BUSINESS_REMINDER_LOADING = 'dashboard/DASHBOARD_SET_BUSINESS_REMINDER_LOADING',
	DASHBOARD_SET_BUSINESS_REMINDER_SUCCESS = 'dashboard/DASHBOARD_SET_BUSINESS_REMINDER_SUCCESS',
	DASHBOARD_DELETE_BUSINESS_REMINDER = 'dashboard/DASHBOARD_DELETE_BUSINESS_REMINDER',
	DASHBOARD_DELETE_BUSINESS_REMINDER_SUCCESS = 'dashboard/DASHBOARD_DELETE_BUSINESS_REMINDER_SUCCESS',
	DASHBOARD_RESET_BUSINESS_REMINDER = 'dashboard/DASHBOARD_RESET_BUSINESS_REMINDER',
	DASHBOARD_REMOVE_MODAL_BUSINESS = 'dashboard/DASHBOARD_REMOVE_MODAL_BUSINESS',

	DASHBOARD_ADD_BUSINESS_DATA = 'dashboard/DASHBOARD_ADD_BUSINESS_DATA',
	DASHBOARD_ADD_BUSINESSES_DATA_LOADING = 'dashboard/DASHBOARD_ADD_BUSINESSES_DATA_LOADING',
	DASHBOARD_ADD_BUSINESSES_DATA_SUCCESS = 'dashboard/DASHBOARD_ADD_BUSINESSES_DATA_SUCCESS',
	DASHBOARD_ADD_BUSINESSES_DATA_FAILURE = 'dashboard/DASHBOARD_ADD_BUSINESSES_DATA_FAILURE',
	DASHBOARD_ADD_BUSINESSES_DATA_RESET = 'dashboard/DASHBOARD_ADD_BUSINESSES_DATA_RESET',
	DASHBOARD_DELETE_BUSINESSES_DATA = 'dashboard/DASHBOARD_DELETE_BUSINESSES_DATA',
	DASHBOARD_DELETE_BUSINESSES_DATA_SUCCESS = 'dashboard/DASHBOARD_DELETE_BUSINESSES_DATA_SUCCESS',
	DASHBOARD_GET_MAPBOX_KEY = 'dashboard/DASHBOARD_GET_MAPBOX_KEY',
	DASHBOARD_GET_MAPBOX_KEY_SUCCESS = 'dashboard/DASHBOARD_GET_MAPBOX_KEY_SUCCESS',
	DASHBOARD_GET_SELECTED_LIST = 'dashboard/DASHBOARD_GET_SELECTED_LIST',
	DASHBOARD_SET_SELECTED_LIST = 'dashboard/DASHBOARD_SET_SELECTED_LIST',

	DASHBOARD_ADD_ONE_TO_SAVE_LIST = 'dashboard/DASHBOARD_ADD_ONE_TO_SAVE_LIST',
	DASHBOARD_ADD_MANY_TO_SAVE_LIST = 'dashboard/DASHBOARD_ADD_MANY_TO_SAVE_LIST',
	DASHBOARD_REMOVE_ONE_FROM_SAVE_LIST = 'dashboard/DASHBOARD_REMOVE_ONE_FROM_SAVE_LIST',
	DASHBOARD_REMOVE_MANY_FROM_SAVE_LIST = 'dashboard/DASHBOARD_REMOVE_MANY_FROM_SAVE_LIST',
	DASHBOARD_REMOVE_ALL_FROM_SAVE_LIST = 'dashboard/DASHBOARD_REMOVE_ALL_FROM_SAVE_LIST',

	DASHBOARD_CLOSE_BUSINESS_PREVIEW = 'dashboard/DASHBOARD_CLOSE_BUSINESS_PREVIEW',

	DASHBOARD_GET_COMMUNICATION_CENTER_BUSINESSES = 'dashboard/DASHBOARD_GET_COMMUNICATION_CENTER_BUSINESSES',
	DASHBOARD_GET_COMMUNICATION_CENTER_BUSINESSES_SUCCESS = 'dashboard/DASHBOARD_GET_COMMUNICATION_CENTER_BUSINESSES_SUCCESS',

	DASHBOARD_GET_SELECT_BUSINESS = 'dashboard/DASHBOARD_GET_SELECT_BUSINESS',
	DASHBOARD_UNSELECT_BUSINESSES = 'dashboard/DASHBOARD_UNSELECT_BUSINESSES',
	DASHBOARD_GET_SELECT_BUSINESS_SUCCESS = 'dashboard/DASHBOARD_GET_SELECT_BUSINESS_SUCCESS',
	DASHBOARD_GET_BUSINESS_OPEN_NOTE = 'dashboard/DASHBOARD_GET_BUSINESS_OPEN_NOTE',
	DASHBOARD_GET_BUSINESS_CLOSE_NOTE = 'dashboard/DASHBOARD_GET_BUSINESS_CLOSE_NOTE',

	EMAIL_GET_EMAIL_THREAD = 'email/EMAIL_GET_EMAIL_THREAD',
	EMAIL_GET_EMAIL_THREAD_SUCCESS = 'email/EMAIL_GET_EMAIL_THREAD_SUCCESS',
	EMAIL_GET_EMAIL_THREAD_LOADING = 'email/EMAIL_GET_EMAIL_THREAD_LOADING',
	EMAIL_GET_EMAIL_THREAD_FAILURE = 'email/EMAIL_GET_EMAIL_THREAD_FAILURE',

	DASHBOARD_OPEN_EMAIL_PREVIEW = 'dashboard/DASHBOARD_OPEN_EMAIL_PREVIEW',
	DASHBOARD_OPEN_EMAIL_PREVIEW_CLEAR = 'dashboard/DASHBOARD_OPEN_EMAIL_PREVIEW_CLEAR',

	DASHBOARD_GET_GOOGLE_PLACES = 'dashboard/DASHBOARD_GET_GOOGLE_PLACES',
	DASHBOARD_GET_GOOGLE_PLACES_SUCCESS = 'dashboard/DASHBOARD_GET_GOOGLE_PLACES_SUCCESS',
	DASHBOARD_GET_GOOGLE_PLACES_FAILURE = 'dashboard/DASHBOARD_GET_GOOGLE_PLACES_FAILURE',
	DASHBOARD_CLEAR_GOOGLE_PLACES = 'dashboard/DASHBOARD_CLEAR_GOOGLE_PLACES',

	DASHBOARD_REQUEST_NEW_LOCATION = 'dashboard/DASHBOARD_REQUEST_NEW_LOCATION',
	DASHBOARD_REQUEST_NEW_LOCATION_SUCCESS = 'dashboard/DASHBOARD_REQUEST_NEW_LOCATION_SUCCESS',
	DASHBOARD_REQUEST_NEW_LOCATION_FAILURE = 'dashboard/DASHBOARD_REQUEST_NEW_LOCATION_FAILURE',
	DASHBOARD_REQUEST_NEW_LOCATION_CLEAR_STATUS = 'dashboard/DASHBOARD_REQUEST_NEW_LOCATION_CLEAR_STATUS',

	DASHBOARD_REQUEST_ADD_LOCATION_FEATURE = 'dashboard/DASHBOARD_REQUEST_ADD_LOCATION_FEATURE',
	DASHBOARD_REQUEST_ADD_LOCATION_FEATURE_SUCCESS = 'dashboard/DASHBOARD_REQUEST_ADD_LOCATION_FEATURE_SUCCESS',
	DASHBOARD_REQUEST_ADD_LOCATION_FEATURE_FAILURE = 'dashboard/DASHBOARD_REQUEST_ADD_LOCATION_FEATURE_FAILURE',
	DASHBOARD_REQUEST_ADD_LOCATION_FEATURE_CLEAR_STATUS = 'dashboard/DASHBOARD_REQUEST_ADD_LOCATION_FEATURE_CLEAR_STATUS',

	DASHBOARD_GET_ACTIVITIES = 'DASHBOARD_GET_ACTIVITIES',
	DASHBOARD_GET_ACTIVITIES_LOADING = 'DASHBOARD_GET_ACTIVITIES_LOADING',
	DASHBOARD_GET_ACTIVITIES_SUCCESS = 'DASHBOARD_GET_ACTIVITIES_SUCCESS',
	DASHBOARD_GET_ACTIVITIES_FAILURE = 'DASHBOARD_GET_ACTIVITIES_FAILURE',

	DASHBOARD_CREATE_ACTIVITY_NOTE = 'DASHBOARD_CREATE_ACTIVITY_NOTE',
	DASHBOARD_CREATE_ACTIVITY_NOTE_LOADING = 'DASHBOARD_CREATE_ACTIVITY_NOTE_LOADING',
	DASHBOARD_CREATE_ACTIVITY_NOTE_SUCCESS = 'DASHBOARD_CREATE_ACTIVITY_NOTE_SUCCESS',
	DASHBOARD_CREATE_ACTIVITY_NOTE_FAILURE = 'DASHBOARD_CREATE_ACTIVITY_NOTE_FAILURE',
	DASHBOARD_CREATE_ACTIVITY_NOTE_CLEAR = 'DASHBOARD_CREATE_ACTIVITY_NOTE_CLEAR',

	DASHBOARD_ACTIVITY_SEARCH = 'DASHBOARD_ACTIVITY_SEARCH',

	DASHBOARD_GET_ACTIVITY_EMAIL_OUTBOUND = 'DASHBOARD_GET_ACTIVITY_EMAIL_OUTBOUND',
	DASHBOARD_GET_ACTIVITY_EMAIL_OUTBOUND_LOADING = 'DASHBOARD_GET_ACTIVITY_EMAIL_OUTBOUND_LOADING',
	DASHBOARD_GET_ACTIVITY_EMAIL_OUTBOUND_SUCCESS = 'DASHBOARD_GET_ACTIVITY_EMAIL_OUTBOUND_SUCCESS',
	DASHBOARD_GET_ACTIVITY_EMAIL_OUTBOUND_FAILURE = 'DASHBOARD_GET_ACTIVITY_EMAIL_OUTBOUND_FAILURE',

	DASHBOARD_GET_ACTIVITY_EMAIL_INBOUND = 'DASHBOARD_GET_ACTIVITY_EMAIL_INBOUND',
	DASHBOARD_GET_ACTIVITY_EMAIL_INBOUND_LOADING = 'DASHBOARD_GET_ACTIVITY_EMAIL_INBOUND_LOADING',
	DASHBOARD_GET_ACTIVITY_EMAIL_INBOUND_SUCCESS = 'DASHBOARD_GET_ACTIVITY_EMAIL_INBOUND_SUCCESS',
	DASHBOARD_GET_ACTIVITY_EMAIL_INBOUND_FAILURE = 'DASHBOARD_GET_ACTIVITY_EMAIL_INBOUND_FAILURE',

	DASHBOARD_CLEAR_ACTIVITY_DATA = 'DASHBOARD_CLEAR_ACTIVITY_DATA',

	DASHBOARD_CLICK_DELETE_NOTE = 'DASHBOARD_CLICK_DELETE_NOTE',
	DASHBOARD_CLICK_DELETE_NOTE_CLEAR = 'DASHBOARD_CLICK_DELETE_NOTE_CLEAR',

	DASHBOARD_SELECT_BUSINESS_TAB = 'DASHBOARD_SELECT_BUSINESS_TAB',

	DASHBOARD_GET_ALL_BUSINESSES = 'dashboard/DASHBOARD_GET_ALL_BUSINESSES',
	DASHBOARD_GET_ALL_BUSINESSES_LOADING = 'dashboard/DASHBOARD_GET_ALL_BUSINESSES_LOADING',
	DASHBOARD_GET_ALL_BUSINESSES_SUCCESS = 'dashboard/DASHBOARD_GET_ALL_BUSINESSES_SUCCESS',

	DASHBOARD_SET_BUSINESSES_COUNT = 'dashboard/DASHBOARD_SET_BUSINESSES_COUNT',
	DASHBOARD_CLEAR_BUSINESSES = 'dashboard/DASHBOARD_CLEAR_BUSINESSES',
	DASHBOARD_CLEAR_CURSORS = 'dashboard/DASHBOARD_CLEAR_CURSORS'
}

interface DashboardAction {
	type: DashboardActionTypes;
	payload?: any;
	error?: any;
}

export {
	DashboardAction,
	BusinessContact,
	DashboardActionTypes,
	Location,
	LocationCoordinates,
	Hours,
	Business,
	BusinessQuery,
	UserNotes,
	SearchType,
	Search,
	FilterType,
	Filter,
	List,
	ContactType,
	defaultContactType,
	FilterQueryType,
	Activity,
	UserAddedData
};
